import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Badge, Button, ButtonGroup, Card, Col, Form, ProgressBar, Row, Spinner,
} from 'react-bootstrap';
import PageHeader from '../components/PageHeader';
import { adminApi, extractError } from '../api/client';
import { formatDateUS } from '../utils/dates';
import { useToast } from '../contexts/ToastContext';

// Partisan primary parties we expect to see. Order matters for display.
const PARTIES = ['DEM', 'REP', 'LIB', 'GRE'];

const PARTY_VARIANT = {
  DEM: 'primary',     // blue
  REP: 'danger',      // red
  LIB: 'warning',
  GRE: 'success',
};

const STATUS_VARIANT = {
  pending: 'secondary',
  unopposed: 'info',
  won_primary: 'success',
  lost_primary: 'dark',
};

const STATUS_LABEL = {
  pending: 'Pending',
  unopposed: 'Unopposed',
  won_primary: 'Winner',
  lost_primary: 'Eliminated',
};

/**
 * /admin/elections/:id/reconcile — Post-primary reconciliation page.
 *
 * Reads the grouped reconciliation view from the backend, lets admin
 * pick the winner per (race, party) group, and posts decisions in one
 * batch. Unopposed primaries can be resolved in one click via the
 * Auto-Unopposed action.
 */
export default function PrimaryReconciliationPage() {
  const { id: electionId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [election, setElection] = useState(null);
  const [groups, setGroups] = useState([]);
  const [summary, setSummary] = useState({ races: 0, groups: 0, pending_groups: 0, resolved_groups: 0 });
  const [selections, setSelections] = useState({});   // { [groupKey]: winner_candidate_id }
  const [filter, setFilter] = useState('pending');    // 'pending' | 'all' | 'resolved'
  const [partyFilter, setPartyFilter] = useState('all');
  const [search, setSearch] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await adminApi.get(`/admin/elections/${electionId}/reconciliation`);
      const data = res.data?.data || {};
      setElection(data.election || null);
      setGroups(data.groups || []);
      setSummary(data.summary || { races: 0, groups: 0, pending_groups: 0, resolved_groups: 0 });
      // Pre-seed selections from current_winner_candidate_id so the radios
      // reflect already-applied decisions on reload.
      const seed = {};
      (data.groups || []).forEach((g) => {
        const k = `${g.race_id}__${g.party}`;
        if (g.current_winner_candidate_id) {
          seed[k] = g.current_winner_candidate_id;
        }
      });
      setSelections(seed);
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  }, [electionId]);

  useEffect(() => {
    if (electionId) {
      loadData();
    }
  }, [electionId, loadData]);

  const filteredGroups = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (groups || []).filter((g) => {
      if (partyFilter !== 'all' && g.party !== partyFilter) return false;
      if (filter === 'pending' && !g.unresolved) return false;
      if (filter === 'resolved' && g.unresolved) return false;
      if (term) {
        const hay = `${g.race_label} ${g.office_name} ${(g.candidates || []).map(c => c.candidate_name).join(' ')}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  }, [groups, filter, partyFilter, search]);

  const dirtyCount = useMemo(() => {
    return groups.filter((g) => {
      const k = `${g.race_id}__${g.party}`;
      const chosen = selections[k];
      return chosen && chosen !== g.current_winner_candidate_id;
    }).length;
  }, [groups, selections]);

  const handleSelectWinner = (group, candidateId) => {
    const k = `${group.race_id}__${group.party}`;
    setSelections((prev) => ({ ...prev, [k]: candidateId }));
  };

  const handleSaveAll = async () => {
    const decisions = [];
    groups.forEach((g) => {
      const k = `${g.race_id}__${g.party}`;
      const chosen = selections[k];
      if (chosen && chosen !== g.current_winner_candidate_id) {
        decisions.push({
          race_id: g.race_id,
          party: g.party,
          winner_candidate_id: chosen,
        });
      }
    });
    if (decisions.length === 0) {
      toast.info('No changes to apply.');
      return;
    }
    setSaving(true);
    try {
      const res = await adminApi.post(
        `/admin/elections/${electionId}/reconciliation`,
        { decisions },
      );
      const body = res.data || {};
      toast.success(
        `Applied ${body.summary?.applied_count || 0} decisions. ${body.summary?.error_count || 0} errors.`,
      );
      await loadData();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setSaving(false);
    }
  };

  const handleAutoUnopposed = async () => {
    setSaving(true);
    try {
      const res = await adminApi.post(
        `/admin/elections/${electionId}/reconciliation/auto-unopposed`,
      );
      toast.success(res.data?.detail || 'Done');
      await loadData();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="container py-4 text-center" data-testid="reconcile-loading">
        <Spinner animation="border" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-4">
        <Alert variant="danger" data-testid="reconcile-error">{error}</Alert>
        <Button variant="secondary" onClick={() => navigate(-1)}>← Back</Button>
      </div>
    );
  }

  const progress = summary.groups > 0
    ? Math.round((summary.resolved_groups / summary.groups) * 100)
    : 0;

  return (
    <div className="container-fluid py-3" data-testid="reconcile-page">
      <PageHeader
        title="Primary Reconciliation"
        subtitle={election ? `${election.name} — ${formatDateUS(election.date)}` : ''}
        actions={
          <ButtonGroup>
            <Button
              variant="outline-secondary"
              size="sm"
              as={Link}
              to={`/admin/elections/${electionId}`}
              data-testid="back-to-election-btn"
            >
              ← Back to Election
            </Button>
          </ButtonGroup>
        }
      />

      <Row className="g-3 mb-3">
        <Col md={6}>
          <Card className="shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between mb-2">
                <strong>Reconciliation Progress</strong>
                <span className="text-muted small">
                  {summary.resolved_groups} of {summary.groups} groups resolved
                </span>
              </div>
              <ProgressBar
                now={progress}
                label={`${progress}%`}
                variant={progress === 100 ? 'success' : 'info'}
                data-testid="progress-bar"
              />
              <div className="mt-2 small text-muted">
                {summary.races} races · {summary.pending_groups} pending decisions
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="shadow-sm">
            <Card.Body>
              <strong className="d-block mb-2">Bulk Actions</strong>
              <div className="d-flex gap-2 flex-wrap">
                <Button
                  variant="info"
                  size="sm"
                  onClick={handleAutoUnopposed}
                  disabled={saving}
                  data-testid="auto-unopposed-btn"
                >
                  {saving && <Spinner size="sm" className="me-2" />}
                  Auto-mark Unopposed
                </Button>
                <Button
                  variant="success"
                  size="sm"
                  onClick={handleSaveAll}
                  disabled={saving || dirtyCount === 0}
                  data-testid="save-decisions-btn"
                >
                  {saving && <Spinner size="sm" className="me-2" />}
                  Apply {dirtyCount > 0 ? `${dirtyCount} ` : ''}Pending Decision{dirtyCount === 1 ? '' : 's'}
                </Button>
              </div>
              <div className="mt-2 small text-muted">
                &ldquo;Auto-mark Unopposed&rdquo; flags any race with a single candidate per party as the primary winner — safe and idempotent.
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card className="shadow-sm mb-3">
        <Card.Body>
          <Row className="g-2 align-items-end">
            <Col md={3}>
              <Form.Label className="small text-muted mb-1">Show</Form.Label>
              <Form.Select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                data-testid="filter-status"
              >
                <option value="pending">Pending only</option>
                <option value="resolved">Resolved only</option>
                <option value="all">All groups</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label className="small text-muted mb-1">Party</Form.Label>
              <Form.Select
                value={partyFilter}
                onChange={(e) => setPartyFilter(e.target.value)}
                data-testid="filter-party"
              >
                <option value="all">All Parties</option>
                {PARTIES.map((p) => (<option key={p} value={p}>{p}</option>))}
              </Form.Select>
            </Col>
            <Col md={6}>
              <Form.Label className="small text-muted mb-1">Search</Form.Label>
              <Form.Control
                placeholder="Filter by office, district, or candidate name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="filter-search"
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Groups */}
      {filteredGroups.length === 0 ? (
        <Alert variant="light" className="text-center" data-testid="no-groups">
          No groups match the current filters.
        </Alert>
      ) : (
        <Row className="g-3" data-testid="groups-list">
          {filteredGroups.map((g) => {
            const key = `${g.race_id}__${g.party}`;
            const chosen = selections[key];
            const isDirty = chosen && chosen !== g.current_winner_candidate_id;
            return (
              <Col md={6} lg={4} key={key}>
                <Card
                  className={`shadow-sm h-100 ${isDirty ? 'border-warning' : ''}`}
                  data-testid={`group-card-${key}`}
                >
                  <Card.Header className="d-flex align-items-center justify-content-between">
                    <div className="text-truncate" title={g.race_label}>
                      <strong>{g.race_label}</strong>
                    </div>
                    <Badge bg={PARTY_VARIANT[g.party] || 'secondary'}>{g.party}</Badge>
                  </Card.Header>
                  <Card.Body>
                    {g.unopposed && (
                      <Alert variant="info" className="py-1 px-2 small mb-2">
                        Unopposed (single candidate)
                      </Alert>
                    )}
                    {!g.unresolved && (
                      <Alert variant="success" className="py-1 px-2 small mb-2">
                        Resolved
                      </Alert>
                    )}
                    {g.unresolved && (
                      <Alert variant="warning" className="py-1 px-2 small mb-2">
                        Pending — pick a winner
                      </Alert>
                    )}
                    <Form>
                      {g.candidates.map((c) => {
                        const statusBadge = c.result_status && STATUS_LABEL[c.result_status]
                          ? <Badge bg={STATUS_VARIANT[c.result_status] || 'secondary'} className="ms-2">{STATUS_LABEL[c.result_status]}</Badge>
                          : null;
                        return (
                          <Form.Check
                            type="radio"
                            id={`${key}__${c.candidate_id}`}
                            key={c.candidate_id}
                            name={key}
                            checked={chosen === c.candidate_id}
                            onChange={() => handleSelectWinner(g, c.candidate_id)}
                            data-testid={`winner-radio-${key}-${c.candidate_id}`}
                            label={
                              <span>
                                <strong>{c.candidate_name}</strong>
                                {c.incumbent && <Badge bg="warning" text="dark" className="ms-2 small">Incumbent</Badge>}
                                {statusBadge}
                              </span>
                            }
                          />
                        );
                      })}
                    </Form>
                    {isDirty && (
                      <div className="mt-2 small text-warning fw-bold">
                        Unsaved change — click &ldquo;Apply&rdquo; above to commit.
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}
    </div>
  );
}
